// Central client state: auth, Reading Basket, preferences, and a story cache.
// Writes happen only on explicit user actions (save, remove, reorder, mark
// read, change preference) — never on hover or carousel movement.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { GoogleAuthProvider, onAuthStateChanged, signInWithPopup, signOut, type User } from "firebase/auth";
import type { Preferences, Story } from "../../shared/model.ts";
import { SAMPLE_STORIES } from "../../shared/sampleStories.ts";
import * as B from "../lib/basket.ts";
import { config, getServices } from "../lib/firebase.ts";
import { clearGuestBasket, loadGuestBasket, loadGuestPrefs, saveGuestBasket, saveGuestPrefs } from "../lib/guestStore.ts";
import { FirestoreStoryRepository, SampleStoryRepository, type StoryRepository } from "../lib/repository.ts";
import { loadAccountBasket, mergeGuestIntoAccount, persistBasketDiff, savePreferences } from "../lib/userData.ts";
import { FEATURES } from "../lib/features.ts";
import { SOURCE_LINK_ATTR, recordClick } from "../lib/clicks.ts";
import type { ClickKind } from "../../shared/clicks.ts";

export type SyncState = "guest" | "syncing" | "synced" | "error";

interface AppStateValue {
  repo: StoryRepository | null;
  configErrors: string[];
  authAvailable: boolean;
  user: Pick<User, "uid" | "displayName" | "email"> | null;
  signIn(): Promise<void>;
  signOutUser(): Promise<void>;
  syncState: SyncState;
  /** True once auth has resolved and any account basket has loaded. */
  basketReady: boolean;
  notice: string | null;
  dismissNotice(): void;

  basket: B.Basket;
  isSaved(id: string): boolean;
  toggleSave(id: string): void;
  /** Adds stories in order (skips ones already saved; stops at the basket limit). Returns how many were added. */
  saveAll(ids: string[]): number;
  remove(id: string): void;
  move(id: string, delta: -1 | 1): void;
  setRead(id: string, isRead: boolean): void;
  markProgress(id: string, progress: number): void;

  prefs: Preferences;
  setPrefs(patch: Partial<Preferences>): void;

  stories: Map<string, Story>;
  rememberStories(list: Story[]): void;
  unavailableIds: Set<string>;
  basketOpen: boolean;
  setBasketOpen(open: boolean): void;
  /** Counts an anonymous click toward "popular" (no-op with bundled samples). */
  trackClick(storyId: string, kind: ClickKind): void;
}

const Ctx = createContext<AppStateValue | null>(null);

const TIMEOUT_MESSAGE =
  config.mode === "emulator"
    ? "Can't reach the Firestore emulator. Start it with `npm run emulators`, then run `npm run seed`."
    : "Couldn't reach the database. Check your connection and try again.";

export function AppStateProvider({ children }: { children: ReactNode }) {
  const services = useMemo(getServices, []);
  const repo = useMemo<StoryRepository | null>(() => {
    if (config.mode === "sample") return new SampleStoryRepository(SAMPLE_STORIES);
    return services ? new FirestoreStoryRepository(services.db, TIMEOUT_MESSAGE) : null;
  }, [services]);

  const [user, setUser] = useState<AppStateValue["user"]>(null);
  const [syncState, setSyncState] = useState<SyncState>("guest");
  const [authResolved, setAuthResolved] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [basket, setBasket] = useState<B.Basket>(loadGuestBasket);
  const [prefs, setPrefsState] = useState<Preferences>(loadGuestPrefs);
  const [stories, setStories] = useState<Map<string, Story>>(() => new Map());
  const [unavailableIds, setUnavailable] = useState<Set<string>>(() => new Set());
  const [basketOpen, setBasketOpen] = useState(false);

  const basketRef = useRef(basket);
  basketRef.current = basket;
  const prefsRef = useRef(prefs);
  prefsRef.current = prefs;
  const userRef = useRef(user);
  userRef.current = user;

  // ---- Auth & guest → account merge (runs once per sign-in) ----
  useEffect(() => {
    // Sign-in parked: stay guest-only and ignore any persisted session.
    if (!services || !FEATURES.signIn) return;
    return onAuthStateChanged(services.auth, async (u) => {
      setAuthResolved(true);
      if (!u) {
        setUser(null);
        setSyncState("guest");
        setBasket(loadGuestBasket());
        setPrefsState(loadGuestPrefs());
        return;
      }
      setUser({ uid: u.uid, displayName: u.displayName, email: u.email });
      setSyncState("syncing");
      try {
        const guest = loadGuestBasket();
        const result = await mergeGuestIntoAccount(services.db, u.uid, guest, loadGuestPrefs());
        clearGuestBasket();
        setBasket(result.basket);
        setPrefsState(result.prefs);
        setSyncState("synced");
        if (result.mergedCount > 0)
          setNotice(`Added ${result.mergedCount} saved ${result.mergedCount === 1 ? "story" : "stories"} from this device to your account.`);
      } catch (err) {
        console.error(err);
        setSyncState("error");
        setNotice("Signed in, but your basket couldn't sync. Guest saves are still on this device.");
      }
    });
  }, [services]);

  // ---- Basket mutations ----
  const update = useCallback(
    (fn: (b: B.Basket) => B.Basket) => {
      const before = basketRef.current;
      const after = fn(before);
      if (after === before) return;
      basketRef.current = after;
      setBasket(after);
      const u = userRef.current;
      if (!u || !services) {
        saveGuestBasket(after);
        return;
      }
      persistBasketDiff(services.db, u.uid, before, after).catch(async (err) => {
        console.error(err);
        setSyncState("error");
        setNotice("That change couldn't be saved to your account. Reloaded your saved basket.");
        try {
          const fresh = await loadAccountBasket(services.db, u.uid);
          basketRef.current = fresh;
          setBasket(fresh);
          setSyncState("synced");
        } catch {
          /* keep error state */
        }
      });
    },
    [services],
  );

  const trackClick = useCallback((storyId: string, kind: ClickKind) => services && recordClick(services.db, storyId, kind), [services]);
  const saveAll = useCallback(
    (ids: string[]) => {
      const before = basketRef.current.length;
      const wasSaved = new Set(basketRef.current.map((b) => b.storyId));
      update((b) => ids.reduce((acc, id) => B.addStory(acc, id), b));
      for (const id of ids) if (!wasSaved.has(id) && B.isSaved(basketRef.current, id)) trackClick(id, "saves");
      return basketRef.current.length - before;
    },
    [update, trackClick],
  );
  const toggleSave = useCallback(
    (id: string) => {
      const adding = !B.isSaved(basketRef.current, id);
      update((b) => (adding ? B.addStory(b, id) : B.removeStory(b, id)));
      // Counts toward "popular" only if it was actually added (not at the basket limit).
      if (adding && B.isSaved(basketRef.current, id)) trackClick(id, "saves");
    },
    [update, trackClick],
  );

  // ---- Preferences: local immediately, account writes debounced ----
  const prefTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setPrefs = useCallback(
    (patch: Partial<Preferences>) => {
      const next = { ...prefsRef.current, ...patch };
      prefsRef.current = next;
      setPrefsState(next);
      const u = userRef.current;
      if (!u || !services) return saveGuestPrefs(next);
      if (prefTimer.current) clearTimeout(prefTimer.current);
      prefTimer.current = setTimeout(() => {
        savePreferences(services.db, u.uid, prefsRef.current).catch((err) => {
          console.error(err);
          setNotice("Couldn't save your preferences to your account.");
        });
      }, 1500);
    },
    [services],
  );

  // ---- Story cache: fetch basket stories not already loaded, in bounded batches ----
  const rememberStories = useCallback((list: Story[]) => {
    setStories((prev) => {
      if (list.every((s) => prev.get(s.id) === s)) return prev;
      const next = new Map(prev);
      list.forEach((s) => next.set(s.id, s));
      return next;
    });
  }, []);

  const requested = useRef(new Set<string>());
  useEffect(() => {
    if (!repo) return;
    const missing = basket.map((b) => b.storyId).filter((id) => !stories.has(id) && !requested.current.has(id));
    if (!missing.length) return;
    missing.forEach((id) => requested.current.add(id));
    repo
      .getByIds(missing)
      .then((found) => {
        rememberStories([...found.values()]);
        const gone = missing.filter((id) => !found.has(id));
        if (gone.length) setUnavailable((prev) => new Set([...prev, ...gone]));
      })
      .catch((err) => {
        console.error(err);
        missing.forEach((id) => requested.current.delete(id));
      });
  }, [basket, stories, repo, rememberStories]);

  // Links out to a story's source carry data-source-of="<story id>"; count them
  // wherever they are (cards, story pages, Map, roundup, shared lists), including
  // middle-clicks.
  useEffect(() => {
    if (!services) return;
    const onClick = (e: MouseEvent) => {
      if (e.type === "auxclick" && e.button !== 1) return;
      const a = (e.target as Element | null)?.closest?.(`a[${SOURCE_LINK_ATTR}]`);
      const id = a?.getAttribute(SOURCE_LINK_ATTR);
      if (id) recordClick(services.db, id, "sources");
    };
    document.addEventListener("click", onClick, true);
    document.addEventListener("auxclick", onClick, true);
    return () => {
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("auxclick", onClick, true);
    };
  }, [services]);

  const value: AppStateValue = {
    repo,
    configErrors: config.errors,
    authAvailable: !!services,
    user,
    async signIn() {
      if (!services) return;
      try {
        await signInWithPopup(services.auth, new GoogleAuthProvider());
      } catch (err) {
        const code = (err as { code?: string }).code ?? "";
        if (code !== "auth/popup-closed-by-user" && code !== "auth/cancelled-popup-request")
          setNotice("Sign-in didn't complete. You can keep browsing as a guest.");
      }
    },
    async signOutUser() {
      if (services) await signOut(services.auth);
    },
    syncState,
    basketReady: !services || !FEATURES.signIn || (authResolved && syncState !== "syncing"),
    notice,
    dismissNotice: () => setNotice(null),
    basket,
    isSaved: (id) => B.isSaved(basket, id),
    toggleSave,
    saveAll,
    remove: (id) => update((b) => B.removeStory(b, id)),
    move: (id, d) => update((b) => B.move(b, id, d)),
    setRead: (id, r) => update((b) => B.setRead(b, id, r)),
    markProgress: (id, p) => update((b) => B.setProgress(b, id, p)),
    prefs,
    setPrefs,
    stories,
    rememberStories,
    unavailableIds,
    basketOpen,
    setBasketOpen,
    trackClick,
  };

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useApp(): AppStateValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useApp must be used inside AppStateProvider");
  return v;
}

